import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import _get from 'lodash/get';
import _uniqBy from 'lodash/uniqBy';

import TransactionDetailModal from './TransactionDetailModal';
import ReverseAuthorizationModal from './ReverseAuthorizationModal';
import FeeWaiverModal from './FeeWaiverModal';
import CondensedTable from 'components/CondensedTable/CondensedTable';
import useAccountQuery from 'hooks/useAccountQuery';
import useAnalytics from 'hooks/useAnalytics';
import useModalControls from 'hooks/useModalControls';
import { ANALYTICS_EVENTS } from 'utilities/constants';
import { formatDate } from 'utilities/datesAndTimes';
import { formatDollarsFromCents } from 'utilities/helpers';
import { ACTION_KEY, getTableColumns } from './transactionTableUtils.jsx';

const TransactionTable = ({
  showSelectionActions = true,
  showAdditionalActions = false,
  enableOTBRelease = false,
  length,
  rowSelection,
  transactions = [],
  ...rest
}) => {
  const { trackEvent } = useAnalytics();
  const [focusedTransaction, setFocusedTransaction] = useState();
  const { accountId, customerId } = useParams();
  const { account, accountInDebtSale } = useAccountQuery(customerId, accountId);

  const unfocusTransaction = () => setFocusedTransaction(undefined);

  const transactionDetailModal = useModalControls({
    onHide: unfocusTransaction,
  });
  const reverseAuthorizationModal = useModalControls({
    onHide: unfocusTransaction,
  });
  const feeWaiverModal = useModalControls({
    onHide: unfocusTransaction,
  });

  const columns = useMemo(() => {
    const openDetailModal = (transaction) => {
      setFocusedTransaction(transaction);
      transactionDetailModal.show();
      trackEvent(ANALYTICS_EVENTS.TRANSACTION_DETAIL_OPENED);
    };

    const handleMenuItemClick = (e, transaction) => {
      switch (e.key) {
        case ACTION_KEY.REVERSE_AUTHORIZATION:
          setFocusedTransaction(transaction);
          reverseAuthorizationModal.show();
          break;
        case ACTION_KEY.WAIVE_FEE:
          setFocusedTransaction(transaction);
          feeWaiverModal.show();
          break;
        default:
      }
    };

    return getTableColumns(
      transactions,
      showAdditionalActions,
      accountInDebtSale,
      enableOTBRelease,
      {
        defaultAction: openDetailModal,
        menuItemAction: handleMenuItemClick,
      },
    );
  }, [
    transactions,
    accountInDebtSale,
    enableOTBRelease,
    showAdditionalActions,
  ]);

  const buildCopyableTransactionText = (selectedRows) => {
    const selectedRowData = selectedRows?.map((row) => {
      return {
        date: formatDate(row.date, true),
        channel: row.merchantChannel,
        amount: formatDollarsFromCents(row.amount),
        status: row.state,
        type: row.type,
      };
    });

    const formattedSingleRowData = selectedRowData.map((row) =>
      Object.values(row).join('   '),
    );
    return Object.values(formattedSingleRowData).join('\n');
  };
  return (
    <>
      <ReverseAuthorizationModal
        {...reverseAuthorizationModal}
        account={account}
        transaction={focusedTransaction}
      />

      <FeeWaiverModal
        {...feeWaiverModal}
        account={account}
        transaction={focusedTransaction}
      />

      <TransactionDetailModal
        {...transactionDetailModal}
        transaction={focusedTransaction}
      />

      <CondensedTable
        data-testid="transactionsTable"
        showSelectionActions={showSelectionActions}
        columns={columns}
        data={transactions}
        length={length || transactions?.length}
        rowSelection={rowSelection}
        onCopy={buildCopyableTransactionText}
        {...rest}
      />
    </>
  );
};

export default TransactionTable;
